import { Head, router } from "@inertiajs/react"
import { useMemo, useState } from "react"
import { PageLayout } from "@/components/VocationalTest/PageLayout"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { CheckCircle, ChevronDown, ArrowDown, Tag, CreditCard, QrCode } from "lucide-react"
import logo from "@/assets/logo-25segundos-new.png"
import valorIcon from "@/assets/valor-icon-new.png"

// Desconto PIX em percentual
const PIX_DISCOUNT_PERCENT = 5

// Produto base vindo do banco (via props)
interface BaseProduct {
  id: number
  slug: string
  name: string
  description: string
  price_cents: number
  installment_price_cents: number | null
  max_installments: number
  grants_advanced_test: boolean
  note?: string
}

interface ComboProduct {
  id: number
  slug: string
  price_cents: number
  installment_price_cents: number | null
  max_installments: number
}

interface Props {
  baseProducts: BaseProduct[]
  allProducts: ComboProduct[]
  purchasedProducts: Record<number, string> // { base_product_id => paid_at ISO string }
  isColegioObjetivo: boolean
}

// Descrições completas ficam no frontend (conteúdo rico, não muda frequentemente)
const FULL_DESCRIPTIONS: Record<string, string[]> = {
  "combo-1": [
    "O que contém:",
    "✧ Diagnóstico aprofundado",
    "✧ Virtudes, paixões e tendências",
    "✧ Forças e bloqueios pessoais",
    "✧ Influências emocionais",
    "✧ Caminhos de evolução",
    "✧ Áreas e profissões compatíveis",
    "✧ Carreira mais provável para o seu perfil",
    "✧ E uma segunda etapa de teste para gerar um resultado ainda mais detalhado",
  ],
  "combo-2": [
    "O que contém:",
    "",
    "Este combo reúne o curso presencial completo e o livro oficial, formando uma jornada estruturada para desenvolver seu projeto de vida com profundidade, prática e transformação real.",
    "",
    "É ideal para quem busca:",
    "",
    "✧ clareza emocional",
    "✧ direção vocacional",
    "✧ organização de metas",
    "✧ um caminho prático para realizar sonhos",
    "✧ apoio diário para criar novas rotinas, escolhas e comportamentos",
    "",
    "🟣 O que contém (Curso + Livro)",
    "",
    "🎥 Curso — S.E.M.E.A.R Construindo Sonhos",
    "",
    "Um curso completo, dividido em módulos, diretos e transformadores.",
    "",
    "Para quem aprende melhor praticando e aplicando no dia a dia.",
    "",
    "Inclui:",
    "",
    "✧ Aulas sobre cada etapa do método",
    "✧ Exercícios aplicados para clareza emocional e vocacional",
    "✧ Orientações guiadas para construção do projeto de vida",
    "✧ Ferramentas para foco, motivação e constância",
    "",
    "📘 Livro S.E.M.E.A.R Construindo Sonhos",
    "",
    "Um guia prático, organizado e profundo para ajudar você a entender quem é, o que quer e como seguir o caminho com mais leveza, direção e sentido.",
    "",
    "Inclui:",
    "",
    "✧ Exercícios de clareza emocional e mental",
    "✧ Ferramentas de foco e tomada de decisão",
    "✧ Mapas de metas, rotas e escolhas",
    "✧ Atividades práticas para organizar carreira, objetivos e vida",
    "✧ Ferramentas exclusivas SEMEAR para foco e constância",
    "✧ Métodos de alinhamento interno",
    "(sentir → entender → mudar → escolher → agir → realizar)",
  ],
  "combo-3": [
    "É ideal para quem quer ir muito além do teste e realmente entender os mecanismos internos que influenciam carreira, emoções, foco e comportamento.",
    "",
    "O que contém:",
    "✧ Explicação clara dos 9 tipos do eneagrama",
    "✧ Influências emocionais, mentais e comportamentais de cada tipo",
    "✧ Como cada tipo se conecta ao SEMEAR",
    "✧ O que cada perfil ganha, perde, evita e busca na vida e no trabalho",
    "✧ Como cada tipo escolhe carreira",
    "✧ Ferramentas práticas para crescimento e autorregulação emocional",
    "✧ Análises profundas para tomada de decisão adulta e consciente",
    "✧ 225 páginas diagramadas em formato premium",
  ],
  "combo-4": [
    "Ideal para quem deseja construir clareza emocional, vocacional e pessoal, além de apoio direto para metas, rotina, decisões importantes e autoconhecimento.",
    "",
    "O que contém:",
    "✧ 10 sessões individuais online (1h cada)",
    "✧ Acompanhamento totalmente personalizado",
    "✧ Desenvolvimento emocional e vocacional",
    "✧ Estratégias práticas para rotina e organização",
    "✧ Apoio real para tomada de decisões importantes",
    "✧ Construção guiada do projeto de vida",
    "✧ Aplicação profunda do Método S.E.M.E.A.R",
    "✧ Ambiente seguro, acolhedor e profissional",
  ],
}

const formatCents = (cents: number) =>
  (cents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const formatInstallmentsInfo = (product: BaseProduct) => {
  if (product.max_installments > 1 && product.installment_price_cents) {
    const installmentValue = product.installment_price_cents / product.max_installments
    return `${product.max_installments}x R$ ${formatCents(installmentValue)} ou à vista R$ ${formatCents(product.price_cents)}`
  }
  return `à vista R$ ${formatCents(product.price_cents)}`
}

// Formata data ISO para DD/MM/YYYY
const formatPurchaseDate = (isoDate: string) => {
  const date = new Date(isoDate)
  return date.toLocaleDateString("pt-BR")
}

export default function CheckoutPage({ baseProducts, allProducts, purchasedProducts }: Props) {
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>([])
  const [openSlug, setOpenSlug] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [paymentType, setPaymentType] = useState<"avista" | "parcelado">("avista")
  const [paymentMethod, setPaymentMethod] = useState<"credit_card" | "pix">("credit_card")

  // Verifica se o produto base já foi comprado (baseProducts têm id = base_product_id)
  const isPurchased = (productId: number) => !!purchasedProducts[productId]
  const getPurchaseDate = (productId: number) => purchasedProducts[productId]

  // Calcula o combo slug baseado na seleção
  const comboSlug = useMemo(() => {
    if (selectedSlugs.length === 0) return null
    // Extrai os números dos slugs (combo-1, combo-2, etc.) e ordena
    const numbers = selectedSlugs
      .map((slug) => slug.replace("combo-", ""))
      .sort((a, b) => parseInt(a) - parseInt(b))
    return `combo-${numbers.join("-")}`
  }, [selectedSlugs])

  // Calcula o total e economia usando o preço do combo
  const { totalAvista, totalParcelado, totalPix, pixSavings, savings, comboProduct } = useMemo(() => {
    const individualTotal = baseProducts
      .filter((p) => selectedSlugs.includes(p.slug))
      .reduce((sum, p) => sum + p.price_cents, 0)

    const combo = allProducts.find((p) => p.slug === comboSlug)
    const comboPrice = combo?.price_cents || individualTotal
    const comboInstallmentPrice = combo?.installment_price_cents || comboPrice

    // Calcula preço PIX com desconto de 5%
    const pixDiscount = Math.floor(comboPrice * PIX_DISCOUNT_PERCENT / 100)
    const pixPrice = comboPrice - pixDiscount

    return {
      totalAvista: comboPrice,
      totalParcelado: comboInstallmentPrice,
      totalPix: pixPrice,
      pixSavings: pixDiscount,
      savings: individualTotal - comboPrice,
      comboProduct: combo
    }
  }, [baseProducts, allProducts, selectedSlugs, comboSlug])

  // Verifica se pode parcelar (combo tem max_installments > 1)
  const canInstall = useMemo(() => {
    if (!comboProduct) {
      // Se só tem um produto selecionado, verifica se ele pode parcelar
      const selected = baseProducts.filter((p) => selectedSlugs.includes(p.slug))
      if (selected.length === 1) {
        return selected[0].max_installments > 1
      }
      return false
    }
    return comboProduct.max_installments > 1
  }, [comboProduct, baseProducts, selectedSlugs])

  const maxInstallments = useMemo(() => {
    if (comboProduct) return comboProduct.max_installments
    const selected = baseProducts.filter((p) => selectedSlugs.includes(p.slug))
    if (selected.length === 1) return selected[0].max_installments
    return 1
  }, [comboProduct, baseProducts, selectedSlugs])

  const toggleProduct = (slug: string) => {
    setSelectedSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    )
    // Reset payment options when selection changes
    setPaymentType("avista")
    setPaymentMethod("credit_card")
  }

  const handleCheckout = () => {
    if (!comboSlug) return

    setIsSubmitting(true)
    router.post("/checkout", {
      product_slug: comboSlug,
      payment_type: paymentMethod === "pix" ? "avista" : paymentType,
      payment_method: paymentMethod
    }, {
      onError: () => setIsSubmitting(false),
    })
  }

  return (
    <>
      <Head title="Checkout" />
      <PageLayout>
        <div className="w-full max-w-2xl relative">
          <Card className="w-full p-8 md:p-12 shadow-elegant">
            <div className="mb-8 flex justify-center">
              <img src={logo} alt="25 Segundos" className="h-16 w-auto" />
            </div>

            <div className="text-center space-y-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-primary">
                <Sparkles className="w-8 h-8 text-primary-foreground" />
              </div>

              <div>
                <h1 className="text-3xl md:text-4xl font-bold mb-3">Descobriu seu Eneatipo?</h1>
                <p className="text-lg text-muted-foreground">
                  Esta descoberta é apenas o primeiro passo da sua jornada de autoconhecimento.
                </p>
              </div>

              <div className="bg-accent/5 rounded-lg p-6 text-left space-y-4">
                <p className="text-muted-foreground">
                  Agora aprenda o que ninguém te contou: como usar isso para destravar sua vida.
                </p>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-muted-foreground">
                      Entenda os passos práticos que você precisa seguir para sua transformação.
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-muted-foreground">
                      Evite os erros mais comuns do seu tipo e acelere seu crescimento.
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-muted-foreground">
                      Descubra como seu Eneatipo influencia suas escolhas, relações e propósito.
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-6">
                <Button
                  size="lg"
                  className="w-full bg-gradient-primary hover:opacity-90 transition-opacity text-xl px-8 py-8 h-auto shadow-elegant flex items-center justify-center gap-2"
                  onClick={() => {
                    const target = document.getElementById("offer-products")
                    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" })
                  }}
                >
                  Oferta Especial <ArrowDown className="w-6 h-6" />
                </Button>
                <p className="text-sm text-muted-foreground mt-3">Exclusivo para quem fez o teste</p>
              </div>

              <div id="offer-products" className="pt-8 space-y-4 text-left">
                {baseProducts.map((product, index) => {
                  const isOpen = openSlug === product.slug
                  const isChecked = selectedSlugs.includes(product.slug)
                  const fullDescription = FULL_DESCRIPTIONS[product.slug] || []
                  const alreadyPurchased = isPurchased(product.id)
                  const purchaseDate = getPurchaseDate(product.id)

                  return (
                    <div
                      key={product.id}
                      className={`border rounded-lg p-4 bg-background transition-colors ${
                        alreadyPurchased
                          ? "opacity-60 cursor-not-allowed border-success/50"
                          : "cursor-pointer hover:border-primary/50"
                      }`}
                      role="button"
                      tabIndex={alreadyPurchased ? -1 : 0}
                      onClick={() => !alreadyPurchased && toggleProduct(product.slug)}
                      onKeyDown={(event) => {
                        if (!alreadyPurchased && (event.key === "Enter" || event.key === " ")) {
                          event.preventDefault()
                          toggleProduct(product.slug)
                        }
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          id={`product-${product.id}`}
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => !alreadyPurchased && toggleProduct(product.slug)}
                          onClick={(event) => event.stopPropagation()}
                          disabled={alreadyPurchased}
                          className="mt-1 h-4 w-4 accent-primary disabled:opacity-50"
                        />

                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <label
                              htmlFor={`product-${product.id}`}
                              className={`font-semibold text-base ${alreadyPurchased ? "cursor-not-allowed" : "cursor-pointer"}`}
                            >
                              {index + 1}) {product.name}
                            </label>
                            {alreadyPurchased && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success">
                                <CheckCircle className="w-3 h-3" />
                                Já adquirido em {formatPurchaseDate(purchaseDate!)}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-2">{product.description}</p>

                          {/* Nota especial do produto (ex: PRESENCIAL) */}
                          {product.note && (
                            <p className="text-sm text-amber-600 font-medium mt-2 bg-amber-50 p-2 rounded"
                               dangerouslySetInnerHTML={{ __html: product.note }}
                            />
                          )}

                          {fullDescription.length > 0 && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation()
                                setOpenSlug(isOpen ? null : product.slug)
                              }}
                              className="flex items-center gap-2 text-sm text-primary mt-3 hover:underline"
                            >
                              <span className="uppercase text-xs">expandir</span>
                              <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                            </button>
                          )}

                          {isOpen && fullDescription.length > 0 && (
                            <div className="mt-3 space-y-2">
                              <div className="bg-accent/5 rounded-lg p-4">
                                {fullDescription.map((item, idx) => (
                                  <p key={`${product.id}-detail-${idx}`} className="text-sm text-muted-foreground">
                                    {item}
                                  </p>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="mt-3 flex items-center gap-2">
                            <img src={valorIcon} alt="Valor" className="w-6 h-6" />
                            <div>
                              <p className="font-semibold">Valor:</p>
                              <p className="text-lg font-bold">{formatInstallmentsInfo(product)}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              {selectedSlugs.length > 0 && (
                <div className="pt-6 border-t space-y-4">
                  <h3 className="text-lg font-bold text-center">Resumo do Pedido</h3>

                  <div className="space-y-3 bg-accent/10 rounded-lg p-4 text-left">
                    {baseProducts
                      .filter((product) => selectedSlugs.includes(product.slug))
                      .map((product) => (
                        <div
                          key={`summary-${product.id}`}
                          className="border-b border-border/50 pb-3 last:border-0 last:pb-0"
                        >
                          <p className="font-semibold text-sm">{product.name}</p>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{product.description}</p>
                        </div>
                      ))}
                  </div>

                  {/* Mostra economia do combo */}
                  {savings > 0 && (
                    <div className="flex items-center justify-center gap-2 text-success bg-success/10 p-3 rounded-lg">
                      <Tag className="w-5 h-5" />
                      <span className="font-semibold">
                        Economia de R$ {formatCents(savings)} no combo!
                      </span>
                    </div>
                  )}

                  {/* Seletor de método de pagamento (PIX ou Cartão) */}
                  <div className="space-y-3">
                    <p className="font-semibold text-sm text-center">Escolha o método de pagamento:</p>
                    <div className="grid gap-3">
                      {/* Opção PIX com destaque de desconto */}
                      <button
                        type="button"
                        onClick={() => {
                          setPaymentMethod("pix")
                          setPaymentType("avista")
                        }}
                        className={`w-full p-4 rounded-lg border-2 transition-colors relative ${
                          paymentMethod === "pix"
                            ? "border-green-500 bg-green-50"
                            : "border-border hover:border-green-500/50"
                        }`}
                      >
                        <div className="absolute -top-2.5 left-4 px-2 py-0.5 rounded bg-green-500 text-white text-xs font-bold">
                          {PIX_DISCOUNT_PERCENT}% OFF
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <QrCode className={`w-8 h-8 ${paymentMethod === "pix" ? "text-green-600" : "text-muted-foreground"}`} />
                            <div className="text-left">
                              <p className="font-semibold">PIX</p>
                              <p className="text-xs text-muted-foreground">Pagamento instantâneo</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground line-through">R$ {formatCents(totalAvista)}</p>
                            <p className="text-xl font-bold text-green-600">R$ {formatCents(totalPix)}</p>
                            <p className="text-xs text-green-600 font-medium">
                              Você economiza R$ {formatCents(pixSavings)}
                            </p>
                          </div>
                        </div>
                      </button>

                      {/* Opção Cartão de Crédito */}
                      <button
                        type="button"
                        onClick={() => setPaymentMethod("credit_card")}
                        className={`w-full p-4 rounded-lg border-2 transition-colors ${
                          paymentMethod === "credit_card"
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/50"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <CreditCard className={`w-8 h-8 ${paymentMethod === "credit_card" ? "text-primary" : "text-muted-foreground"}`} />
                            <div className="text-left">
                              <p className="font-semibold">Cartão de Crédito</p>
                              <p className="text-xs text-muted-foreground">
                                {canInstall ? `Em até ${maxInstallments}x` : "À vista"}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            {canInstall ? (
                              <>
                                <p className="text-xl font-bold text-primary">
                                  {maxInstallments}x R$ {formatCents(totalParcelado / maxInstallments)}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  ou R$ {formatCents(totalAvista)} à vista
                                </p>
                              </>
                            ) : (
                              <p className="text-xl font-bold text-primary">R$ {formatCents(totalAvista)}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    </div>

                    {/* Sub-opção: à vista ou parcelado (só para cartão) */}
                    {paymentMethod === "credit_card" && canInstall && (
                      <div className="flex gap-2 pl-4">
                        <button
                          type="button"
                          onClick={() => setPaymentType("avista")}
                          className={`flex-1 p-2 rounded-lg border transition-colors text-sm ${
                            paymentType === "avista"
                              ? "border-primary bg-primary/10 font-semibold"
                              : "border-border hover:border-primary/50"
                          }`}
                        >
                          À vista: R$ {formatCents(totalAvista)}
                        </button>
                        <button
                          type="button"
                          onClick={() => setPaymentType("parcelado")}
                          className={`flex-1 p-2 rounded-lg border transition-colors text-sm ${
                            paymentType === "parcelado"
                              ? "border-primary bg-primary/10 font-semibold"
                              : "border-border hover:border-primary/50"
                          }`}
                        >
                          {maxInstallments}x R$ {formatCents(totalParcelado / maxInstallments)}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between items-center pt-2">
                    <span className="text-lg font-semibold">Total:</span>
                    <span className={`text-2xl font-bold ${paymentMethod === "pix" ? "text-green-600" : ""}`}>
                      {paymentMethod === "pix"
                        ? `R$ ${formatCents(totalPix)}`
                        : canInstall && paymentType === "parcelado"
                          ? `${maxInstallments}x R$ ${formatCents(totalParcelado / maxInstallments)}`
                          : `R$ ${formatCents(totalAvista)}`
                      }
                    </span>
                  </div>
                  <Button
                    size="lg"
                    className={`w-full transition-opacity text-lg px-8 py-6 h-auto shadow-elegant ${
                      paymentMethod === "pix"
                        ? "bg-green-600 hover:bg-green-700"
                        : "bg-gradient-primary hover:opacity-90"
                    }`}
                    onClick={handleCheckout}
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? "Processando..."
                      : paymentMethod === "pix"
                        ? "Pagar com PIX"
                        : "Finalizar Compra"
                    }
                  </Button>
                </div>
              )}

              <div className="pt-4">
                <Button
                  size="lg"
                  variant="ghost"
                  className="w-full text-sm text-muted-foreground hover:text-foreground"
                  onClick={() => router.visit("/results")}
                >
                  Nesse momento me contento com esse resultado
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </PageLayout>
    </>
  )
}

const Sparkles = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
  </svg>
)
